"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function BlogListConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  if (loading && posts.length === 0) {
    return <div data-section-id="236476" className="grid grid-cols-1 md:grid-cols-2 gap-16">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className={cn("flex flex-col gap-4", i % 2 !== 0 ? "md:mt-[60px]" : "")}>
            <div className="w-full aspect-[4/3] bg-muted/30 animate-pulse rounded-xl" />
            <div className="h-6 w-24 bg-muted/30 animate-pulse rounded-full mt-4" />
            <div className="h-8 w-3/4 bg-muted/30 animate-pulse rounded mt-2" />
            <div className="h-20 w-full bg-muted/30 animate-pulse rounded mt-2" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-destructive mb-4">Failed to load articles.</p>
        <Button variant="outline" onClick={async () => await loadMore()}>Try Again</Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center text-muted-foreground py-12 font-sans">
        No articles found at the moment. Please check back later.
      </div>;
  }
  return <div className="flex flex-col items-center">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-16 w-full">
        {posts.map((post, i) => {
        const category = post.categories?.[0]?.name || "Insights";
        const date = post.date ? new Date(post.date).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        }) : "Recent";
        return <article key={post.id} data-index={i} className={cn("group flex flex-col h-full", i % 2 !== 0 ? "md:mt-[60px]" : "")}>
              <Link to={post.link || "#"} className="block overflow-hidden rounded-xl mb-6">
                <AspectRatio ratio={4 / 3} className="bg-muted">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" /> : <div className="w-full h-full flex items-center justify-center bg-muted/50 text-muted-foreground font-sans">
                      No Image Available
                    </div>}
                </AspectRatio>
              </Link>

              <div className="flex flex-col flex-grow">
                <div className="mb-4">
                  <Badge variant="outline" className="text-primary border-primary/30 uppercase tracking-widest text-[10px] font-bold px-3 py-1 rounded-full bg-primary/5">
                    {category}
                  </Badge>
                </div>

                <Link to={post.link || "#"} className="block group-hover:text-primary transition-colors duration-300">
                  <h3 className="font-default text-2xl md:text-3xl font-bold leading-tight mb-4 text-foreground" dangerouslySetInnerHTML={{
                __html: post.title
              }} />
                </Link>

                <div className="font-sans text-muted-foreground line-clamp-3 mb-8 text-lg leading-relaxed" dangerouslySetInnerHTML={{
              __html: post.excerpt || "Discover professional insights and industry-leading perspectives in our latest featured article."
            }} />

                <div className="mt-auto flex items-center justify-between pt-6 border-t border-border/40">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground font-sans">
                    <span>{date}</span>
                    <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
                    <span>4 min read</span>
                  </div>

                  <Link to={post.link || "#"} className="inline-flex items-center gap-2 font-sans text-sm font-bold uppercase tracking-widest text-primary group-hover:text-primary/80 transition-colors relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-[2px] after:bg-primary/30 after:transition-all after:duration-300 group-hover:after:bg-primary">
                    Read More
                  </Link>
                </div>
              </div>
            </article>;
      })}
      </div>

      {hasNext && <div className="mt-24">
          <Button variant="outline" size="lg" onClick={async () => await loadMore()} disabled={isRefetching} className="font-sans uppercase tracking-widest font-bold border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full px-8 h-12">
            {isRefetching ? "Loading..." : "View More Insights"}
          </Button>
        </div>}
    </div>;
}
export default function BlogList() {
  const wp_query_params = {
    post_type: "post",
    per_page: 8,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="featured-blog-posts" className="py-24 md:pb-[120px] bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="mb-16">
          <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground mb-4">Latest Perspectives</h2>
          <p className="font-sans text-muted-foreground text-xl max-w-2xl">Expert analysis and professional updates from our dedicated team of industry specialists.</p>
        </div>
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 8,
        order: "desc",
        orderby: "date"
      }}>
          <BlogListConsumer />
        </PostsProvider>
      </div>
    </section>;
}